/**
 * Matriz de permissões do workspace.
 *
 * Fonte única da verdade do RBAC. Módulo puro: não conhece banco, sessão nem HTTP —
 * por isso é testável sem subir nada, e é onde o teste de permissão realmente mora.
 *
 * A checagem acontece no servidor (ver `workspaceProcedure`). O frontend usa isto
 * apenas para *esconder* o que o usuário não pode fazer; esconder não é proteger.
 */

export const WORKSPACE_ROLES = ['OWNER', 'ADMIN', 'ANALYST', 'MEMBER'] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

/** Ordem de poder. Usada só para comparações de hierarquia, nunca para autorizar. */
const ROLE_RANK: Record<WorkspaceRole, number> = {
  OWNER: 4,
  ADMIN: 3,
  ANALYST: 2,
  MEMBER: 1,
};

export const PERMISSIONS = [
  // workspace
  'workspace:read',
  'workspace:update',
  'workspace:delete',
  'workspace:transfer_ownership',
  // membros
  'member:read',
  'member:invite',
  'member:update_role',
  'member:remove',
  // faturamento
  'billing:read',
  'billing:manage',
  /**
   * Alterar o orçamento de campanha/conjunto NA META (06/10). Gasta dinheiro do cliente,
   * por isso é só do OWNER — a mesma linha de `billing:manage`.
   */
  'ads:budget',
  // monitoradores (Fase 2)
  'monitor:read',
  'monitor:create',
  'monitor:update',
  'monitor:delete',
  'monitor:connect',
  // grupos (Fase 3)
  'group:read',
  'group:import',
  'group:update',
  'group:archive',
  /**
   * Baixar a lista de CONTATOS de um grupo — telefone em claro, em arquivo.
   *
   * Permissão própria, e não `group:read`, porque são coisas de risco diferente: ver a
   * tela mostra códigos pseudônimos; o CSV leva a base de telefones para fora do sistema,
   * de onde nenhuma rotação de sal a alcança mais. É o mesmo patamar de `pii:reveal` — e
   * fica separada dele de propósito, para poder ser tirada de ADMIN sem tirar junto a
   * revelação de um número avulso.
   */
  'group:export_contacts',
  // campanhas (Fase 4)
  'campaign:read',
  'campaign:create',
  'campaign:update',
  'campaign:archive',
  'campaign:delete',
  // redirecionadores (Fase 5)
  'redirector:read',
  'redirector:create',
  'redirector:update',
  'redirector:delete',
  /**
   * B2–B4 — a página de passagem do link: pixels (Meta, GA4, GTM, Pinterest) e a prévia OG
   * (título, descrição, imagem). Só OWNER e ADMIN (requisito B4.2): é o que roda no navegador
   * de quem clica e o que aparece no card do WhatsApp — quem cria link (ANALYST) não injeta
   * rastreador de terceiro no tráfego pago do workspace.
   */
  'redirector:page',
  // analytics
  'analytics:read',
  'analytics:export',
  /**
   * Lançar a comissão do dia (o número que a pessoa lê na Shopee e digita aqui).
   *
   * Permissão própria, e **não** `billing:manage`: `billing` é a fatura DESTE produto — quem
   * a mexe mexe no dinheiro que o workspace paga. Isto é dado de operação, o faturamento do
   * cliente, digitado todo dia por quem toca o dia a dia. Amarrá-lo ao OWNER faria a métrica
   * principal do painel depender do dono estar disponível todo fim de expediente.
   *
   * Ler continua em `analytics:read` (todo mundo vê o painel); só escrever é restrito, porque
   * um número errado aqui não quebra nada e vira decisão de compra de tráfego em silêncio.
   */
  'earnings:write',
  /**
   * Conectar, trocar ou remover a credencial da Shopee Affiliate Open API (F1.4).
   *
   * Só do OWNER: a senha da API dá acesso ao relatório de comissões da conta de afiliado
   * inteira — é dinheiro do cliente, na mesma linha de `billing:manage` e `ads:budget`.
   * Lançar e corrigir a comissão do dia continua em `earnings:write`.
   */
  'earnings:connect',
  // dado pessoal — sempre auditado
  'pii:reveal',
  // operação
  'incident:read',
  'incident:acknowledge',
  'audit:read',
  // integrações
  'integration:read',
  'integration:manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * MEMBER: enxerga a operação, não muda nada.
 * ANALYST: MEMBER + exporta e reconhece incidente. É o papel de quem cuida do dia a dia.
 * ADMIN: opera o produto inteiro, menos o que afeta dinheiro e a existência do workspace.
 * OWNER: tudo.
 */
const MEMBER: Permission[] = [
  'workspace:read',
  'member:read',
  'monitor:read',
  'group:read',
  'campaign:read',
  'redirector:read',
  'analytics:read',
  'incident:read',
  'integration:read',
];

const ANALYST: Permission[] = [
  ...MEMBER,
  'analytics:export',
  /** Quem cuida do dia a dia é quem lança a comissão do dia. Ver o comentário na permissão. */
  'earnings:write',
  'incident:acknowledge',
  'campaign:create',
  'campaign:update',
  'redirector:create',
  'redirector:update',
];

const ADMIN: Permission[] = [
  ...ANALYST,
  'workspace:update',
  'member:invite',
  'member:update_role',
  'member:remove',
  'monitor:create',
  'monitor:update',
  'monitor:delete',
  'monitor:connect',
  'group:import',
  'group:update',
  'group:archive',
  'campaign:archive',
  'campaign:delete',
  'redirector:delete',
  'redirector:page',
  'pii:reveal',
  /**
   * Fica em ADMIN, ao lado de `pii:reveal`, e não em ANALYST: `analytics:export` exporta
   * NÚMERO, isto exporta GENTE. Quem já pode revelar telefone um a um é quem pode levar a
   * lista inteira — dar a bulk quem não tem o avulso seria a hierarquia ao contrário.
   */
  'group:export_contacts',
  'audit:read',
  'integration:manage',
  'billing:read',
];

const OWNER: Permission[] = [
  ...ADMIN,
  'ads:budget',
  'earnings:connect',
  'workspace:delete',
  'workspace:transfer_ownership',
  'billing:manage',
];

export const ROLE_PERMISSIONS: Record<WorkspaceRole, ReadonlySet<Permission>> = {
  MEMBER: new Set(MEMBER),
  ANALYST: new Set(ANALYST),
  ADMIN: new Set(ADMIN),
  OWNER: new Set(OWNER),
};

export function can(role: WorkspaceRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].has(permission);
}

export function canAll(role: WorkspaceRole, permissions: readonly Permission[]): boolean {
  return permissions.every((p) => can(role, p));
}

/** `true` se `actor` tem posto estritamente maior que `target`. */
export function outranks(actor: WorkspaceRole, target: WorkspaceRole): boolean {
  return ROLE_RANK[actor] > ROLE_RANK[target];
}

/**
 * Regra à parte, porque é onde sistemas de RBAC costumam se machucar.
 *
 * Existe **exatamente um OWNER por workspace** — garantido no banco por índice parcial
 * `UNIQUE (workspace_id) WHERE role = 'OWNER'`. Logo, OWNER não se rebaixa nem se remove:
 * a única saída é `workspace:transfer_ownership`, que promove o novo dono e rebaixa o
 * antigo na mesma transação. Sem isso, um workspace ficaria órfão — com assinatura ativa
 * e ninguém autorizado a mexer nela.
 */
export function canManageMember(args: {
  actorRole: WorkspaceRole;
  targetRole: WorkspaceRole;
  actorIsSelf: boolean;
}): { allowed: boolean; reason?: string } {
  const { actorRole, targetRole, actorIsSelf } = args;

  if (targetRole === 'OWNER') {
    return {
      allowed: false,
      reason: actorIsSelf
        ? 'Transfira a propriedade do workspace antes de alterar seu próprio acesso.'
        : 'O proprietário só muda por transferência de propriedade.',
    };
  }
  if (!can(actorRole, 'member:update_role')) {
    return { allowed: false, reason: 'Seu papel não permite gerenciar membros.' };
  }
  if (!actorIsSelf && !outranks(actorRole, targetRole)) {
    return { allowed: false, reason: 'Você não pode gerenciar alguém de papel igual ou superior.' };
  }
  return { allowed: true };
}

/**
 * Transferência de propriedade: única operação que troca o OWNER.
 * Deve rodar numa transação que rebaixa o atual e promove o novo — nunca em dois passos,
 * senão o índice parcial rejeita o segundo e o workspace fica sem dono.
 */
export function canTransferOwnership(actorRole: WorkspaceRole, targetRole: WorkspaceRole): boolean {
  return can(actorRole, 'workspace:transfer_ownership') && targetRole !== 'OWNER';
}
